import { createSignal, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { useToast } from "../components/MessageBox";
import { useSFetch } from "../hooks/sfetch";
import { useApi, isOk } from '../api';
import Text from '../components/Text';
import { firstError } from '../api/envelope';
import { useTranslation } from '../hooks/useTranslation';

export default function OAuthAuthorize() {
    const toast = useToast();
    const navigate = useNavigate();
    const sfetch = useSFetch();
    const api = useApi();
    const { t } = useTranslation();
    
    const [clientId, setClientId] = createSignal<string>("");
    const [clientName, setClientName] = createSignal<string>("");
    const [redirectUri, setRedirectUri] = createSignal<string>("");
    const [scope, setScope] = createSignal<string>("");
    const [isLoading, setIsLoading] = createSignal<boolean>(true);
    const [redirectAfterGrant, setRedirectAfterGrant] = createSignal<string>("");

    onMount(async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const clientIdParam = urlParams.get("client_id") || "";
        const redirectUriParam = urlParams.get("thirdPartyRedirect") || "";
        const redirectAfterGrant = urlParams.get("redirectUri") || "";
        const scopeParam = urlParams.get("scope") || "";

        setClientId(clientIdParam);
        setRedirectUri(redirectUriParam);
        setScope(scopeParam);
        setRedirectAfterGrant(redirectAfterGrant);

        if (!clientIdParam || !redirectUriParam) {
            toast?.error(t("OAUTH_AUTHORIZE_MISSING_PARAMS"));
            navigate("/dashboard");
            return;
        }

        try {
            const response = await api.oauth.getClientName(clientIdParam);
            if (isOk(response.data)) {
                setClientName(response.data.value?.clientName || t("OAUTH_AUTHORIZE_UNKNOWN_CLIENT"));
            } else {
                toast?.error(firstError(response.data) ?? t("OAUTH_AUTHORIZE_CLIENT_LOAD_FAILED"));
                navigate("/dashboard");
            }
        } catch (error: any) {
            toast?.error(error?.message ?? t("OAUTH_AUTHORIZE_CLIENT_LOAD_FAILED"));
            navigate("/dashboard");
        } finally {
            setIsLoading(false);
        }
    });

    async function handleAuthorize(e: Event) {
        e.preventDefault();

        await sfetch(`/api/user/oauth/grant`, {
            method: "POST",
            body: JSON.stringify({
                clientId: clientId(),
                scope: scope()
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        window.location.href = redirectAfterGrant();
    }

    function handleDeny(e: Event) {
        e.preventDefault();
        document.location.href = redirectUri() + "?error=access_denied";
    }

    return (
        <div class="centered">
            <div class="form" style={{ "max-width": "500px", "text-align": "center" }}>
                <h1><Text val="OAUTH_AUTHORIZE_HEADER" /></h1>
                {isLoading() ? (
                    <p><Text val="LOADING" />...</p>
                ) : (
                    <>
                        <h2><Text val="AUTHORIZATION_REQUEST" /></h2>
                        <p>
                            <Text val="APPLICATION_REQUESTING_ACCESS" /> <strong>{clientName()}</strong> <Text val="TO_YOUR_ACCOUNT" />.
                        </p>
                        {scope() && (
                            <p>
                                <strong><Text val="REQUESTED_SCOPE" />:</strong> {scope()}
                            </p>
                        )}
                        <p>
                            <Text val="DO_YOU_WANT_TO_GRANT_ACCESS" />
                        </p>
                        <div class="h" style={{ "justify-content": "center" }}>
                            <button
                                type="button"
                                class="btn btn-success"
                                onClick={handleAuthorize}
                            >
                                <Text val="YES_GRANT_ACCESS" />
                            </button>
                            <button
                                type="button"
                                class="btn btn-danger"
                                onClick={handleDeny}
                            >
                                <Text val="NO_DENY_ACCESS" />
                            </button>
                        </div>
                        <div style={{ "margin-top": "var(--spacing-xl)" }}>
                            <small class="gray">
                                <Text val="APPLICATION" />: {clientName()}<br/>
                                {redirectUri() && <><Text val="REDIRECT_URI" />: {redirectUri()}</>}
                            </small>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
